/* eslint-disable react-hooks/rules-of-hooks */
import {
  SpeciesName,
  SpeciesDataCard,
  AverageContentDataCard,
  Badge,
  makeTitleCase,
} from '../../components'
import type { FullSpecies, Reference, ReferencedData } from '../../types'
import { createClient } from '../../util/supabase/component'
import {
  type QueryFunctionContext,
  queryOptions,
  useSuspenseQuery,
} from '@tanstack/react-query'
import { createFileRoute, Link, useParams } from '@tanstack/react-router'
import { queryClient } from '../../util/queryclient'
import {
  SpeciesDetailProvider,
  useSpeciesDetail,
} from '@/context/speciesDetail'

const averageDataGroups: Array<
  keyof Omit<ReferencedData, 'germination' | 'dispersal'>
> = ['seed_weights', 'oil_content', 'protein_content']

const forbiddenMorphologyFields = ['id', 'species_id', 'reference_id']

const speciesQueryFn = async ({ queryKey }: QueryFunctionContext<string[]>) => {
  const supabase = createClient()
  const uuid = queryKey[1]
  const { data, error } = await supabase
    .from('species')
    .select(
      `*,
        family(name),
        germination(
          percent_germ, presow_treatment, temperature, light_hours, days, medium, provenance_country, provenance_detail, sample_size, reference_id
        ),
        dispersal(notes, method, animal_group, animal_species, dispersal_agents(description), provenance_country, provenance_detail, reference_id), 
        storage_behaviour(
          *
        ),
        seed_weights(
          thousandseedweight, notes, material_weighed(description), provenance_country, provenance_detail, reference_id
        ),
        protein_content(
          protein_content, moisture_status, notes, material_weighed(description), provenance_country, provenance_detail, reference_id
        ),
        oil_content(
          oil_content, moisture_status, notes, material_weighed(description), provenance_country, provenance_detail, reference_id
        ),
        morphology(
          *
        )
        `,
    )
    .eq('id', uuid)
  if (error) throw new Error(error.message)
  return data[0] as unknown as FullSpecies
}

const References = ({ references }: { references: Reference[] }) => {
  const { hoveredRef } = useSpeciesDetail()
  if (!references?.length) return null

  return (
    <div className="sm:w-100 mt-2 block rounded-lg border border-teal-200 bg-white p-6 ">
      <h5 className="mb-2 text-2xl font-bold tracking-tight text-gray-900">
        References
      </h5>
      <ol className="list-decimal pl-4 font-normal text-gray-700">
        {references.map((item) => {
          const highlighted = item.id === hoveredRef?.id
          return (
            <li
              className={`${
                highlighted
                  ? 'border border-gray-200 backdrop-blur bg-serblue-light bg-opacity-10 p-1 rounded shadow'
                  : 'p-1 border border-white'
              }`}
              key={item.id}
            >
              {item.description}
            </li>
          )
        })}
      </ol>
    </div>
  )
}

const SpeciesDetail = ({
  species,
  references,
}: {
  species: FullSpecies
  references: Reference[]
}) => {
  return (
    <SpeciesDetailProvider species={species} references={references}>
      <div className="mt-2">
        <Link to="/">
          <span className="cursor-pointer text-sm italic">
            ← Back to last search results
          </span>
        </Link>
        {species && (
          <>
            <div className="mt-6 flex-col border-b-2 border-serblue-light p-2 sm:w-full lg:w-1/2">
              <SpeciesName species={species} withAuthor className="text-2xl" />
              {species.common_name && (
                <div className="py-1 text-xs uppercase">
                  {species.common_name}
                </div>
              )}
            </div>
            <div className="text-md p-2 ">
              <span className="uppercase">
                {species.family.name} · {species.genus}
              </span>{' '}
              | <span className="italic">{species.synonyms}</span>
            </div>
            {species.storage_behaviour &&
              species.storage_behaviour.length > 0 && (
                <div className="sm:w-100 mt-2 block rounded-lg border border-teal-200 bg-white p-6 ">
                  <h5 className="mb-2 text-2xl font-bold tracking-tight text-gray-900">
                    Storage Behaviour
                  </h5>
                  <Badge
                    title={species.storage_behaviour[0].storage_behaviour}
                  />
                  {species.storage_behaviour[0].distribution && (
                    <>
                      <h5 className="mt-1 text-lg font-medium">Distribution</h5>
                      <span>{species.storage_behaviour[0].distribution}</span>
                    </>
                  )}
                  {species.storage_behaviour[0].storage_conditions && (
                    <>
                      <h5 className="mt-1 text-lg font-medium">Conditions</h5>
                      <span>
                        {species.storage_behaviour[0].storage_conditions}
                      </span>
                    </>
                  )}
                </div>
              )}
            {species.germination?.length > 0 && (
              <SpeciesDataCard
                title={'germination'}
                data={species.germination}
              />
            )}
            {species.dispersal?.length > 0 && (
              <SpeciesDataCard title={'dispersal'} data={species.dispersal} />
            )}
            {averageDataGroups
              .filter((dataGroup) => species[dataGroup]?.length > 0)
              .map((dataGroup) => (
                <AverageContentDataCard
                  key={dataGroup}
                  title={dataGroup}
                  data={species[dataGroup]}
                />
              ))}
            {species.morphology && species.morphology.length > 0 && (
              <div className="sm:w-100 mt-2 block rounded-lg border border-teal-200 bg-white p-6 ">
                <h5 className="mb-2 text-2xl font-bold tracking-tight text-gray-900">
                  Morphology
                </h5>
                {species.morphology.map((morph, i) => (
                  <ul
                    key={`morph-ul-${i}`}
                    className="list-disc pl-4 font-normal text-gray-700"
                  >
                    {Object.entries(morph)
                      .filter(
                        ([key, value]) =>
                          value && !forbiddenMorphologyFields.includes(key),
                      )
                      .map(([key, value]) => (
                        <li key={`morph-${key}`}>
                          <span className="font-bold">
                            {makeTitleCase(key)}
                          </span>
                          : {value}
                        </li>
                      ))}
                  </ul>
                ))}
              </div>
            )}
            <References references={references} />
          </>
        )}
        {!species && (
          <div
            className="mb-4 flex rounded-lg bg-red-100 p-4 text-sm text-red-700 dark:bg-red-200 dark:text-red-800"
            role="alert"
          >
            <svg
              className="mr-3 inline h-5 w-5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              ></path>
            </svg>
            <div>
              <span className="font-medium">Oops</span> We can't find this
              species, sorry. Please{' '}
              <Link to="/">
                <span className="cursor-pointer underline">
                  go back to search
                </span>
              </Link>
            </div>
          </div>
        )}
      </div>
    </SpeciesDetailProvider>
  )
}

const getSpeciesQueryOptions = (uuid: string | string[] | undefined) =>
  queryOptions({
    queryKey: ['species', (Array.isArray(uuid) ? uuid[0] : uuid) ?? ''],
    queryFn: speciesQueryFn,
    enabled: Boolean(uuid),
  })

const referencesQueryFn = async ({
  queryKey,
}: QueryFunctionContext<Array<string | number>>) => {
  const supabase = createClient()
  const [, ...ids] = queryKey
  const { data, error } = await supabase
    .from('references')
    .select('*')
    // @ts-expect-error ids will always be numbers
    .in('id', ids)

  if (error) throw new Error(error.message)
  return data as Reference[]
}

const getReferencesQueryOptions = (lookupIds: number[] = []) => ({
  queryKey: ['references', ...lookupIds],
  queryFn: referencesQueryFn,
  enabled: Boolean(lookupIds.length),
  refetchOnMount: false,
})

const getSpeciesReferenceIds = (species: FullSpecies) =>
  (
    [
      'germination',
      'seed_weights',
      'oil_content',
      'protein_content',
      'dispersal',
      'morphology',
    ] as Array<keyof ReferencedData>
  )
    .flatMap((tableName) => species[tableName].map((item) => item.reference_id))
    .filter(Boolean) as number[]

export const Route = createFileRoute('/species/$uuid')({
  loader: async ({ params }) => {
    const { uuid } = params

    const species = await queryClient.ensureQueryData(
      getSpeciesQueryOptions(uuid),
    )

    const referenceIds = getSpeciesReferenceIds(species)
    const references = await queryClient.ensureQueryData(
      getReferencesQueryOptions(referenceIds),
    )

    return { species, references }
  },
  component: () => {
    const { uuid } = useParams({ strict: true, from: '/species/$uuid' })
    const { data: species } = useSuspenseQuery(getSpeciesQueryOptions(uuid))
    const referenceIds = getSpeciesReferenceIds(species)
    const referenceQueryOptions = getReferencesQueryOptions(referenceIds)
    const { data: references } = useSuspenseQuery(referenceQueryOptions)

    return <SpeciesDetail species={species} references={references} />
  },
})
